<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import axiosInstance from '@/plugins/axios';
import { useSessionStore } from '@/store/sessionStore';
import { getStoredRedirectSessionPayload, pushRedirect } from '@/router/redirect';


const route = useRoute();
const router = useRouter();
const email = ref<string>('');
const password = ref<string>('');
const toast = useToast();
const loginDisabled = ref(false)
const loading = ref(false)
const sessionStore = useSessionStore();


const login = async () => {
  loading.value = true
  try {
    const response = await axiosInstance.post('/auth/login', {
      email: email.value,
      password: password.value
    });

    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('tenant', JSON.stringify(response.data.tenant));
      sessionStore.setToken(response.data.token);
      sessionStore.setUser(response.data.user);
      sessionStore.setTenant(response.data.tenant);
      await pushRedirect(router, route.query.redirect, {
        token: response.data.token,
        user: response.data.user,
        tenant: response.data.tenant
      });
    }
  } catch (error: any) {
    if (error.response && error.response.status === 401) {
        toast.add({ severity: 'error', summary: 'Fehler', detail: error.response.data.message, life: 5000 });
        loading.value = false
      } else {
        toast.add({ severity: 'error', summary: 'Fehler', detail: error.toString(), life: 5000 });
        loading.value = false
      }
  }
};



onMounted(async () => {

  if (localStorage.getItem('token')) {
    loading.value = true
    try {
      await axiosInstance.get('/auth/state', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      await new Promise(r => setTimeout(r, 2000));
      await pushRedirect(router, route.query.redirect, getStoredRedirectSessionPayload());
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        toast.add({ severity: 'error', summary: 'Error', detail: "Session expired. Please log in again.", life: 5000 });
        loading.value = false
      } else {
        toast.add({ severity: 'error', summary: 'Fehler', detail: error.toString(), life: 5000 });
        loading.value = false
      }
    }
  }
});




const onEnterPress = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    login();
  }
};

document.addEventListener('keydown', onEnterPress);


onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEnterPress);
});

</script>

<template>
  <div class="vh-100 d-flex align-items-center justify-content-center">
    <div class="surface-card p-5 shadow-2 border-round" style="max-width: 500px; min-width: 350px;">
      <ProgressBar v-if="loading" mode="indeterminate" class="mb-5" style="height: 6px"></ProgressBar>

      <div class="text-center mb-5">
        <div class="text-900 text-3xl font-medium mb-1">Login</div>
        <div class="text-sm font-medium mb-1">AIRiN Admin-Center</div>

      </div>

      <div>
        <label for="email" class="block text-900 font-medium mb-2">E-Mail</label>
        <InputText id="email" type="text" v-model="email" class="w-full mb-3" />

        <label for="password" class="block text-900 font-medium mb-2">Password</label>
        <Password fluid id="password" :feedback="false" type="password" v-model="password" toggleMask class="w-full" />

        <div class="flex align-items-center justify-content-between mb-6">
          <a class="font-medium no-underline ml-2 text-blue-500 text-right cursor-pointer" @click="router.push('/forgot-password')">Passwort vergessen?</a>
        </div>

        <Button :disabled="loginDisabled" label="Login" icon="pi pi-user" class="w-full" @click="login"></Button>
      </div>
    </div>
  </div>
  <div class="background-overlay"></div>
</template>

<style scoped>
.vh-100 {
  height: 100vh;
}

.d-flex {
  display: flex;
}

.align-items-center {
  align-items: center;
}

.justify-content-center {
  justify-content: center;
}

.background-overlay {
  width: 100%;
  height: 100%;
  position: absolute !important;
  top: 0px !important;
  left: 0px !important;
  right: 0px !important;
  bottom: 0px !important;
  z-index: 1;
  background: black !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.p-button {
  background-color: var(--tuev-light-blue);
  border-color: var(--tuev-light-blue);
}

.p-button:hover {
  background-color: var(--tuev-dark-blue) !important;
  border-color: var(--tuev-dark-blue) !important;
}

.p-progressbar .p-progressbar-value {
  border: 0 none;
  margin: 0;
  background: var(--tuev-light-blue) !important;
}

.surface-card {
  background-color: white !important;
  color: var(--tuev-dark-blue);
  z-index: 2;
}
</style>
